import React, { useState, useEffect } from 'react';
import { commentService } from '../../services/comments';
import { Comment } from '../../types/comment';

interface CommentStats {
  totalComments: number;
  commentsToday: number;
  commentsThisWeek: number;
  uniqueCommenters: number;
}

const AdminCommentManager: React.FC = () => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [stats, setStats] = useState<CommentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const COMMENTS_PER_PAGE = 20;

  useEffect(() => {
    loadComments();
    loadStats();
  }, [currentPage]);

  const loadComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const allComments = await commentService.getAllComments();
      setComments(allComments);
      setTotalPages(Math.ceil(allComments.length / COMMENTS_PER_PAGE));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comments');
      console.error('Error loading comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await commentService.getCommentStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading comment stats:', err);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await commentService.deleteComment(commentId);
      setComments(comments.filter(c => c.id !== commentId));
      setShowDeleteConfirm(null);
      // Reload stats after deletion
      loadStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
      console.error('Error deleting comment:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const filteredComments = comments.filter(comment =>
    comment.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    comment.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    comment.user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    comment.blockId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && comments.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '2rem auto' }}></div>
        <p>Loading comment management...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="card-header">
            <h1 className="card-title">Comment Management</h1>
            <p style={{ color: '#666', margin: 0 }}>
              Review and manage all user comments
            </p>
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem', 
            marginBottom: '2rem' 
          }}>
            <div className="card">
              <div className="card-header">
                <h3 style={{ margin: 0, fontSize: '2rem', color: '#2563eb' }}>
                  {stats.totalComments}
                </h3>
                <p style={{ margin: 0, color: '#666' }}>Total Comments</p>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <h3 style={{ margin: 0, fontSize: '2rem', color: '#059669' }}>
                  {stats.commentsToday}
                </h3>
                <p style={{ margin: 0, color: '#666' }}>Today</p>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <h3 style={{ margin: 0, fontSize: '2rem', color: '#7c3aed' }}>
                  {stats.commentsThisWeek}
                </h3>
                <p style={{ margin: 0, color: '#666' }}>This Week</p>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <h3 style={{ margin: 0, fontSize: '2rem', color: '#dc2626' }}>
                  {stats.uniqueCommenters}
                </h3>
                <p style={{ margin: 0, color: '#666' }}>Unique Users</p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="card-header">
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search comments, users, or sections..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              />
              <button
                onClick={loadComments}
                disabled={loading}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '2rem' }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Comments List */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              Comments ({filteredComments.length} of {comments.length})
            </h2>
          </div>
          
          {filteredComments.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              {searchTerm ? 'No comments match your search.' : 'No comments found.'}
            </div>
          ) : (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {filteredComments.map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    padding: '1rem',
                    borderBottom: '1px solid #eee',
                    backgroundColor: selectedComment?.id === comment.id ? '#f8f9fa' : 'transparent'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <strong>{comment.user.firstName} {comment.user.lastName}</strong>
                        <span style={{ color: '#666', fontSize: '0.875rem' }}>
                          in section {comment.blockId}
                        </span>
                      </div>
                      <div style={{ color: '#666', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                        {formatDate(comment.createdAt)}
                        {comment.updatedAt !== comment.createdAt && (
                          <span> (edited {formatDate(comment.updatedAt)})</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => setSelectedComment(selectedComment?.id === comment.id ? null : comment)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: selectedComment?.id === comment.id ? '#059669' : '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        {selectedComment?.id === comment.id ? 'Hide' : 'View'}
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(comment.id)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '0.75rem', 
                    borderRadius: '4px',
                    borderLeft: '3px solid #2563eb'
                  }}>
                    {comment.content}
                  </div>

                  {selectedComment?.id === comment.id && (
                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '1rem', 
                      backgroundColor: '#f1f5f9', 
                      borderRadius: '4px' 
                    }}>
                      <h4 style={{ margin: '0 0 0.5rem 0' }}>Comment Details</h4>
                      <div style={{ fontSize: '0.875rem', color: '#666' }}>
                        <p><strong>Comment ID:</strong> {comment.id}</p>
                        <p><strong>User ID:</strong> {comment.userId}</p>
                        <p><strong>Block ID:</strong> {comment.blockId}</p>
                        <p><strong>Created:</strong> {formatDate(comment.createdAt)}</p>
                        <p><strong>Last Updated:</strong> {formatDate(comment.updatedAt)}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '1rem', 
            marginTop: '2rem' 
          }}>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: currentPage === 1 ? '#e5e7eb' : '#2563eb',
                color: currentPage === 1 ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>
            
            <span style={{ color: '#666' }}>
              Page {currentPage} of {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: currentPage === totalPages ? '#e5e7eb' : '#2563eb',
                color: currentPage === totalPages ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="card" style={{ maxWidth: '400px', margin: '1rem' }}>
              <div className="card-header">
                <h3 className="card-title">Confirm Deletion</h3>
              </div>
              <div style={{ padding: '1rem' }}>
                <p>Are you sure you want to delete this comment? This action cannot be undone.</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteComment(showDeleteConfirm)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCommentManager;
